import { ComponentKewlox } from './ComponentKewlox';
import {
  CrossPieceEntity,
  PropsComponent,
  PropsGeneral,
  SearchInfo,
} from '../Data';
import {
  CrossPieceType,
  CROSS_PIECE_HEIGHT,
  CROSS_PIECE_ROD_WEIGHT_SUPPL,
  CROSS_PIECE_WOODD_DENSITY_GAP_DD,
  CROSS_PIECE_WOODD_DENSITY_GAP_ED,
  CROSS_PIECE_WOODD_DENSITY_GAP_EP,
  Material,
  Position,
  CROSS_PIECE_DEPTH_TWO_GROOVES,
  CROSS_PIECE_DEPTH_ONE_GROOVE,
} from '../constants';
import { CustomPiece } from './CustomPiece';
import { DataStorage, getPiecesProps } from '../utils/dataStorage';
import {
  computePiecePrice,
  computeWeightWithDensity,
  getPropsKey,
} from '../utils/helpers';

export class CrossPiece extends ComponentKewlox {
  readonly width: number;
  readonly type: CrossPieceType;
  readonly material: Material;
  readonly reinforce: boolean;
  readonly rod: boolean;
  readonly drill: boolean;

  static _lookup = ['width'];

  static _gapWidth = -9;

  constructor(
    id: number,
    name: string,
    reference: string,
    price: number,
    weigth: number,
    forSale: boolean,
    visible: boolean,
    height: number,
    width: number,
    type: CrossPieceType,
    material: Material,
    reinforce: boolean,
    rod: boolean,
    drill?: boolean,
  ) {
    super(id, name, reference, price, weigth, forSale, visible, height);
    this.width = width;
    this.type = type;
    this.material = material;
    this.reinforce = reinforce;
    this.rod = rod;
    this.drill = !!drill;
  }

  get typeComponent() {
    return 'CrossPiece';
  }

  static fromJSON(json: CrossPieceEntity): CrossPiece {
    const obj = Object.create(CrossPiece.prototype);
    return Object.assign(obj, this, json);
  }

  static reviver(key: string, value: any): any {
    return key === '' ? CrossPiece.fromJSON(value) : value;
  }

  compareTo(otherComponent: ComponentKewlox): number {
    return this.width - (otherComponent as CrossPiece).width;
  }

  getKey(): string {
    return CrossPiece.generateKey(
      this.width,
      this.material,
      this.type,
      this.reinforce,
      this.rod,
      this.drill,
    );
  }

  static getSearchInfos(
    width: number,
    material: Material,
    type: CrossPieceType,
    reinforce: boolean,
    rod = false,
    drill = false,
  ): SearchInfo {
    const height = 40;
    return {
      lookup: CrossPiece._lookup,
      key: { height, width, material, type, reinforce, rod, drill },
    };
  }

  static generateKey(
    width: number,
    material: Material,
    type: CrossPieceType,
    reinforce: boolean,
    rod: boolean,
    drill: boolean,
  ) {
    const searchInfos = CrossPiece.getSearchInfos(
      width,
      material,
      type,
      reinforce,
      rod,
      drill,
    );
    return JSON.stringify(searchInfos.key);
  }

  static standardCrossPiece(
    width: number,
    position: Position,
  ): CustomPiece<CrossPiece> {
    /**
     * Return a standard CP as custom piece
     */
    const pieces = DataStorage.pieces;
    const searchInfo = CrossPiece.getSearchInfos(
      width,
      Material.Wood,
      CrossPieceType.UpperPaneLowerPane,
      false,
      false,
      false,
    );
    return pieces.crossPieces.getCustom(searchInfo, position);
  }

  get realHeight() {
    return CrossPiece.computeRealHeight(CROSS_PIECE_HEIGHT);
  }

  get realWidth() {
    return CrossPiece.computeRealWidth(this.width);
  }

  get realDepth() {
    let depth = CROSS_PIECE_DEPTH_ONE_GROOVE;
    if (
      this.type === CrossPieceType.UpperDoorsLowerDoors ||
      this.type === CrossPieceType.UpperEmptyLowerDoors
    )
      depth = CROSS_PIECE_DEPTH_TWO_GROOVES;
    return CrossPiece.computeRealDepth(depth);
  }

  static computeRealWidth(
    width: number,
    glass = false,
    largeDoor = false,
  ): number {
    return width + this._gapWidth;
  }

  static getProperties(material: Material): PropsComponent | void {
    const key = getPropsKey(material);

    if (getPiecesProps().crossPieces.hasOwnProperty(key)) {
      return getPiecesProps().crossPieces[key];
    }
  }

  static computeBasePrice(
    type: CrossPieceType,
    material: Material,
    width: number,
    depth: number,
    rod: boolean,
    drill: boolean,
    props: PropsComponent,
    propsGen: PropsGeneral,
  ) {
    /**
     * This formula came from the PRO100 Excel linked file
     *
     * Wood:
     * ((K1 * K2 * volume) + K3) * K4
     *
     * Alu:
     * ((height * K1) + K2) * K3
     *
     * height and volume in µ
     *
     */
    const heightMicr = CROSS_PIECE_HEIGHT / 1000;
    const widthMicr = width / 1000;
    const depthMicr = depth / 1000;

    const volume = heightMicr * widthMicr * depthMicr;

    const k1 = props.k1 ?? 0;
    const k2 = props.k2 ?? 0;
    const k3 = props.k3 ?? 0;
    const k4 = props.k4 ?? 0;
    const k5 = props.k5 ?? 0;
    const k6 = props.k6 ?? 0;

    let price = 0;
    if (
      material === Material.Wood &&
      (type === CrossPieceType.UpperEmptyLowerPane ||
        type === CrossPieceType.UpperPaneLowerPane)
    )
      price = (volume * k6 * k1 + k2) * k3;
    else if (
      material === Material.Wood &&
      (type === CrossPieceType.UpperEmptyLowerDoors ||
        type === CrossPieceType.UpperDoorsLowerDoors)
    )
      price = (volume * k5 * k1 + k2) * k3;
    else if (
      material === Material.Aluminium &&
      (type === CrossPieceType.UpperPaneLowerPane ||
        type === CrossPieceType.UpperDoorsLowerDoors)
    )
      price = (widthMicr * k5 + k2) * k3;
    else if (
      material === Material.Aluminium &&
      (type === CrossPieceType.UpperEmptyLowerPane ||
        type === CrossPieceType.UpperEmptyLowerDoors)
    )
      price = (widthMicr * k6 + k2) * k3;

    if (type === CrossPieceType.UpperPaneLowerPane)
      price += propsGen.crossPieceTopGrooveSuppl;
    else if (type === CrossPieceType.UpperDoorsLowerDoors)
      price += 2 * propsGen.crossPieceTopGrooveSuppl;

    if (rod) price += propsGen.crossPieceRodSuppl;

    if (drill) {
      let holes = 2;
      if (width > 1000) holes = 3;

      price += holes * propsGen.crossPieceDrillSuppl;
    }

    price *= propsGen.coef;
    price = Math.round(price * 100) / 100;

    return price;
  }

  public computePrice(customPiece: CustomPiece<ComponentKewlox>): number {
    const props: PropsComponent | void = CrossPiece.getProperties(
      this.material,
    );
    const propsGen: PropsGeneral = getPiecesProps().general;

    if (!props) return 0;

    const rod = (customPiece.base as CrossPiece).rod;
    const drill = (customPiece.base as CrossPiece).drill;

    const material: Material = (customPiece.base as CrossPiece).material;
    const type: CrossPieceType = (customPiece.base as CrossPiece).type;

    const priceForDimensions = CrossPiece.computeBasePrice(
      type,
      material,
      customPiece.realWidth,
      customPiece.realDepth,
      rod,
      drill,
      props,
      propsGen,
    );
    const priceForBase = CrossPiece.computeBasePrice(
      type,
      material,
      this.realWidth,
      this.realDepth,
      rod,
      drill,
      props,
      propsGen,
    );

    return computePiecePrice(
      priceForDimensions,
      priceForBase,
      customPiece.custom,
      customPiece.calibrated,
      propsGen,
    );
  }

  public computeWeight(customPiece: CustomPiece<ComponentKewlox>): number {
    const material: Material = (customPiece.base as CrossPiece).material;
    const props: PropsComponent | void = CrossPiece.getProperties(material);

    if (!props) return 0;

    const type: CrossPieceType = (customPiece.base as CrossPiece).type;
    let density = props.density;
    switch (type) {
      case CrossPieceType.UpperDoorsLowerDoors:
        density += CROSS_PIECE_WOODD_DENSITY_GAP_DD;
        break;

      case CrossPieceType.UpperEmptyLowerDoors:
        density += CROSS_PIECE_WOODD_DENSITY_GAP_ED;
        break;

      case CrossPieceType.UpperEmptyLowerPane:
        density += CROSS_PIECE_WOODD_DENSITY_GAP_EP;
        break;

      default:
        break;
    }

    let weight = computeWeightWithDensity(
      customPiece.realWidth,
      customPiece.realHeight,
      customPiece.realDepth,
      density,
    );

    const rod = (customPiece.base as CrossPiece).rod;
    if (rod) weight += CROSS_PIECE_ROD_WEIGHT_SUPPL;

    return weight;
  }
}
